package org.catharsis.spendtel.feature.presentation.web

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowBack
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.automirrored.outlined.Logout
import androidx.compose.material.icons.filled.AccountCircle
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.catharsis.spendtel.feature.presentation.expensereport.SpendTelReportColors

import androidx.navigation.NavHostController
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.core.navigation.showsWebLandingPage
import org.catharsis.spendtel.feature.data.creditsSummaryLine
import org.catharsis.spendtel.feature.presentation.HomeEffect
import org.catharsis.spendtel.feature.presentation.HomeEvent
import org.catharsis.spendtel.feature.presentation.HomeViewModel
import org.koin.compose.viewmodel.koinViewModel

class WebSessionChromeState(
    val userName: String?,
    val email: String?,
    val creditsSummary: String?,
    val accountMenuExpanded: Boolean,
    val onLogoClick: () -> Unit,
    val onAccountMenuToggle: () -> Unit,
    val onAccountMenuDismiss: () -> Unit,
    val onOpenSettings: () -> Unit,
    val onSignOut: () -> Unit,
)

@Composable
fun rememberWebSessionChrome(
    navHostController: NavHostController,
    onLogoClick: () -> Unit = {
        navHostController.navigate(AppRoutes.Home.name) {
            popUpTo(AppRoutes.Home.name) { inclusive = false }
            launchSingleTop = true
        }
    },
): WebSessionChromeState {
    val homeViewModel = koinViewModel<HomeViewModel>()
    val state by homeViewModel.state.collectAsState()
    var accountMenuExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        homeViewModel.effect.collect { effect ->
            if (effect is HomeEffect.OnSignedOut) {
                val destination = if (showsWebLandingPage()) {
                    AppRoutes.Landing.name
                } else {
                    AppRoutes.Authentication.name
                }
                navHostController.navigate(destination) {
                    popUpTo(AppRoutes.Home.name) { inclusive = true }
                }
            }
        }
    }

    return WebSessionChromeState(
        userName = state.currentUser?.username,
        email = state.currentUser?.emailAddress,
        creditsSummary = state.entitlement?.creditsSummaryLine(),
        accountMenuExpanded = accountMenuExpanded,
        onLogoClick = onLogoClick,
        onAccountMenuToggle = { accountMenuExpanded = !accountMenuExpanded },
        onAccountMenuDismiss = { accountMenuExpanded = false },
        onOpenSettings = {
            accountMenuExpanded = false
            navHostController.navigate(AppRoutes.Settings.name)
        },
        onSignOut = {
            accountMenuExpanded = false
            homeViewModel.postEvent(HomeEvent.OnSignOut)
        },
    )
}

internal val webPageMaxWidth = 1140.dp
internal val webCompactBreakpoint = 768.dp

@Composable
fun WebAppChrome(
    userName: String?,
    email: String?,
    creditsSummary: String?,
    accountMenuExpanded: Boolean,
    onLogoClick: () -> Unit,
    onAccountMenuToggle: () -> Unit,
    onAccountMenuDismiss: () -> Unit,
    onOpenSettings: () -> Unit,
    onSignOut: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = Color.White,
        shadowElevation = 1.dp,
    ) {
        Box(
            modifier = Modifier.fillMaxWidth(),
            contentAlignment = Alignment.TopCenter,
        ) {
            Row(
                modifier = Modifier
                    .widthIn(max = webPageMaxWidth)
                    .fillMaxWidth()
                    .padding(horizontal = 24.dp, vertical = 14.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = "SPENDTEL",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    letterSpacing = 2.sp,
                    color = SpendTelReportColors.ink,
                    modifier = Modifier.clickable(onClick = onLogoClick),
                )

                Box {
                    IconButton(onClick = onAccountMenuToggle) {
                        Icon(
                            imageVector = Icons.Filled.AccountCircle,
                            contentDescription = "Account",
                            tint = SpendTelReportColors.inkMuted,
                        )
                    }
                    DropdownMenu(
                        expanded = accountMenuExpanded,
                        onDismissRequest = onAccountMenuDismiss,
                    ) {
                        if (!userName.isNullOrBlank() || !email.isNullOrBlank()) {
                            androidx.compose.foundation.layout.Column(
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                            ) {
                                userName?.let {
                                    Text(
                                        text = it,
                                        style = MaterialTheme.typography.titleSmall,
                                        fontWeight = FontWeight.SemiBold,
                                    )
                                }
                                email?.let {
                                    Text(
                                        text = it,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = SpendTelReportColors.inkMuted,
                                    )
                                }
                                creditsSummary?.let {
                                    Text(
                                        text = it,
                                        style = MaterialTheme.typography.bodySmall,
                                        color = SpendTelReportColors.indigo,
                                        modifier = Modifier.padding(top = 4.dp),
                                    )
                                }
                            }
                            HorizontalDivider()
                        }
                        DropdownMenuItem(
                            text = { Text("Settings") },
                            onClick = onOpenSettings,
                            leadingIcon = {
                                Icon(
                                    imageVector = Icons.Filled.Settings,
                                    contentDescription = null,
                                )
                            },
                        )
                        DropdownMenuItem(
                            text = { Text("Sign out") },
                            onClick = onSignOut,
                            leadingIcon = {
                                Icon(
                                    imageVector = Icons.AutoMirrored.Outlined.Logout,
                                    contentDescription = null,
                                )
                            },
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun WebPageBreadcrumb(
    segments: List<String>,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        segments.forEachIndexed { index, segment ->
            val isLast = index == segments.lastIndex
            Text(
                text = segment,
                style = MaterialTheme.typography.bodySmall,
                color = if (isLast) SpendTelReportColors.ink else SpendTelReportColors.inkMuted,
                fontWeight = if (isLast) FontWeight.SemiBold else FontWeight.Normal,
            )
            if (!isLast) {
                Text(
                    text = "/",
                    style = MaterialTheme.typography.bodySmall,
                    color = SpendTelReportColors.inkMuted.copy(alpha = 0.5f),
                )
            }
        }
    }
}

@Composable
fun WebBackLink(
    label: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Row(
        modifier = modifier
            .clickable(onClick = onClick)
            .padding(vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Icon(
            imageVector = Icons.AutoMirrored.Outlined.ArrowBack,
            contentDescription = null,
            tint = SpendTelReportColors.violet,
            modifier = Modifier.size(18.dp),
        )
        Text(
            text = label,
            style = MaterialTheme.typography.bodyMedium,
            color = SpendTelReportColors.violet,
            fontWeight = FontWeight.Medium,
        )
    }
}

@Composable
fun WebSectionHeader(
    title: String,
    subtitle: String? = null,
    modifier: Modifier = Modifier,
) {
    androidx.compose.foundation.layout.Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.headlineMedium,
            fontWeight = FontWeight.Bold,
            color = SpendTelReportColors.ink,
        )
        subtitle?.let {
            Text(
                text = it,
                style = MaterialTheme.typography.bodyLarge,
                color = SpendTelReportColors.inkMuted,
                lineHeight = MaterialTheme.typography.bodyLarge.lineHeight,
            )
        }
    }
}

@Composable
fun WebAccentCard(
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = Color.White,
        shadowElevation = 1.dp,
        tonalElevation = 0.dp,
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .background(Color.White)
                .padding(20.dp),
        ) {
            content()
        }
    }
}
